import { useSyncExternalStore } from 'react';
import { useUiStore } from '@/store/uiStore.ts';

// Match phones in any orientation, but NOT iPads/tablets.
// (max-width: 500px)                         — portrait phones
// (pointer: coarse) and (hover: none) and (max-height: 500px)
//                                            — landscape phones only (iPads have height > 500px)
// Exported so tests that stub matchMedia (useRegularWidthLayout.test.ts) address THIS query rather
// than a hand-copied string that would go stale the day the query moves.
export const MOBILE_QUERY =
  '(max-width: 500px), ((pointer: coarse) and (hover: none) and (max-height: 500px))';

const mql = typeof window !== 'undefined'
  ? window.matchMedia(MOBILE_QUERY)
  : null;

// 20260920 - THE MediaQueryList 'change' EVENT IS NOT ENOUGH IN THIS WEBVIEW, and this hook was the
// last media-query subscriber in the app still relying on it alone. The same idiom its siblings
// already carry (useRegularWidthLayout.ts subscribeCompactWidth, useAdaptiveGrid.ts): the WebView
// drops MediaQueryList/ResizeObserver deliveries across a rotation, and a fold or unfold is a resize
// that may carry no 'change' at all — so `resize` is the second signal, and 'orientationchange' is
// re-fired on the NEXT animation frame because that event lands BEFORE the new layout.
//
// It matters more here than anywhere else it has been applied: this predicate is the shot-actions
// panel's MOUNT gate (useShotActionsPanelOpen.ts — `permanent` and `admits` are both `!isMobile`),
// so a dropped delivery is a panel that is missing, or stranded on screen, until the next flip.
//
// The PREDICATE is untouched: MOBILE_QUERY is still the whole rule. An extra notification that changes
// nothing is a free React bail-out, exactly as the sibling documents.
function subscribe(cb: () => void) {
  mql?.addEventListener('change', cb);
  if (typeof window === 'undefined') return () => { mql?.removeEventListener('change', cb); };
  let raf = 0;
  const notifyNextFrame = () => {
    if (typeof requestAnimationFrame !== 'function') { cb(); return; }
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(cb);
  };
  window.addEventListener('resize', cb);
  window.addEventListener('orientationchange', notifyNextFrame);
  return () => {
    mql?.removeEventListener('change', cb);
    if (typeof requestAnimationFrame === 'function') cancelAnimationFrame(raf);
    window.removeEventListener('resize', cb);
    window.removeEventListener('orientationchange', notifyNextFrame);
  };
}

// 20260920 (review) - A FRESH matchMedia PER READ, not the singleton's `matches`. The singleton is kept
// for the 'change' subscription only. Nobody has shown that a long-lived MediaQueryList's `matches` is
// live INSIDE a `resize` listener in this WebView - the media-query step of "update the rendering" runs
// AFTER the resize steps - and if it is not, the new resize backstop would re-read the pre-fold answer,
// React would bail out, and the dropped-'change' case this backstop exists for would stay broken. The
// siblings this idiom was copied from (getCompactWidthSnapshot, panelOwnsSelectionNow) already re-query.
function getSnapshot() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(MOBILE_QUERY).matches;
}

/** Returns `true` on phones (any orientation) or when force-mobile is toggled (M key). */
export function useMobileLayout(): boolean {
  const mediaMatch = useSyncExternalStore(subscribe, getSnapshot, () => false);
  const forceMobile = useUiStore(s => s.forceMobile);
  return mediaMatch || forceMobile;
}
